<template>
  <div @contextmenu="showContextMenu">

    <drop class="roomTileContainer elevation-3 drop" @drop="handleRoomDrop" @dragover="handleRoomDragover(...arguments)">
      <v-layout class="roomTileHeader" row @click.stop="showRoomDialog()">
        <v-flex style="width: calc(100% - 120px)">
          <span class="roomName" :style="roomNameStyle">{{room.name}}</span>
          <v-chip v-if="showOccupancyBadge" color="grey" text-color="white" cursor="pointer" disabled>{{ badgeText }}</v-chip>
        </v-flex>
        <v-flex text-xs-right>
          <span class="roomType" :style="roomTypeStyle">{{roomTypeCode|roomTypeDisplayName}}</span>
          <v-tooltip v-if="room.currentNote" top>
            <v-icon class="roomNote" small slot="activator">fas fa-sticky-note</v-icon>
            <span>{{room.currentNote}}</span>
          </v-tooltip>
        </v-flex>
      </v-layout>
      <v-flex class="stayContainer drop" v-if="isBodyVisible">
        <transition-group :name="transitionGroupNameForStayTiles" v-if="transitionGroupNameForStayTiles !== 'off'">
          <drag class="drag" :draggable="isStayActionable(stay)" @dragstart="handleStayDragStart()" @dragend="handleStayDragEnd()" :transfer-data="getDragTransferData(stay)" v-if="room.stays" v-for="stay of room.stays" :key="stay.bedBoardStayID.id">
            <stay-tile class="stayTile elevation-2" :room="room" :stay="stay" :unit="unit"></stay-tile>
          </drag>
        </transition-group>
        <div v-if="transitionGroupNameForStayTiles === 'off'">
          <drag class="drag" :draggable="isStayActionable(stay)" @dragstart="handleStayDragStart()" @dragend="handleStayDragEnd()" :transfer-data="getDragTransferData(stay)" v-if="room.stays" v-for="stay of room.stays" :key="stay.bedBoardStayID.id">
            <stay-tile class="stayTile elevation-2" :room="room" :stay="stay" :unit="unit"></stay-tile>
          </drag>
        </div>
      </v-flex>

    </drop>

    <v-dialog v-if="isRoomDialogVisible" v-model="isRoomDialogVisible" scrollable min-height="90%" width="90%">
      <v-flex>
        <room-card :unit="unit" :room="roomCopy" :isEditable="unit.isRoomEditingAllowed" :onCancel="closeRoomDialog" :onSave="saveRoom"></room-card>
      </v-flex>
    </v-dialog>

    <v-dialog v-if="isAssignClientDialogVisible" v-model="isAssignClientDialogVisible" width="90%">
      <v-flex>
        <assign-client :unit="unit" :room="room" :onCancel="closeAssignClientDialog" :onAssign="assignClient"></assign-client>
      </v-flex>
    </v-dialog>

    <v-dialog v-if="isMoveConfirmationDialogVisible" v-model="isMoveConfirmationDialogVisible" height="300px" width="500px">
      <v-flex>
        <move-confirmation :moveParams="moveParams" :onCancel="closeMoveConfirmationDialog" :onMove="moveStay"></move-confirmation>
      </v-flex>
    </v-dialog>

    <v-menu v-model="isContextMenuVisible" :position-x="x" :position-y="y" absolute offset-y>
      <v-list>
        <v-list-tile @click="showRoomDialog()">Update Room Settings</v-list-tile>
        <v-list-tile @click="showAssignClientDialog()" v-if="unit.isAssignStayAllowed">Assign Client</v-list-tile>
      </v-list>
    </v-menu>

  </div>
</template>

<script lang="js">
import moment from 'moment';

import appSettings from '../appSettings';

import AssignClient from './AssignClient.vue';
import MoveConfirmation from './MoveConfirmation.vue';
import RoomCard from './RoomCard.vue';
import StayTile from './StayTile.vue';
import bedBoardUtils from '../bedBoardUtils';

export default {
  name: 'RoomTile',
  data() {
    return {
      isAssignClientDialogVisible: false,
      isContextMenuVisible: false,
      isDragging: false,
      isMoveConfirmationDialogVisible: false,
      isRoomDialogVisible: false,
      roomCopy: null,
      moveParams: null,
      x: 0,
      y: 0
    };
  },
  props: {
    isBodyVisible: Boolean,
    room: Object,
    unit: Object
  },
  components: {
    'assign-client': AssignClient,
    'move-confirmation': MoveConfirmation,
    'room-card': RoomCard,
    'stay-tile': StayTile,
  },
  computed: {
    badgeText() {
      let returnValue = null;

      if (this.showOccupancyBadge) {
        returnValue = this.$store.getters.getRoomBadgeText(this.room);
      }

      return returnValue;
    },
    roomNameStyle() {
      let returnValue = null;
      if (this.room && this.room.roomStatusCode) {
        const visualizedCodedEntry = this.$store.getters.getRoomStatusCode(this.room.roomStatusCode);
        if (visualizedCodedEntry) {
          returnValue = {
            color: visualizedCodedEntry.foregroundColorHexValue
              ? visualizedCodedEntry.foregroundColorHexValue
              : '#000000',
            display: 'inline-block',
            verticalAlign: 'middle',
            maxWidth: '82%',
            margin: '0',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          };
        }
      }
      return returnValue;
    },
    roomTypeCode() {
      let returnValue = null;
      if (this.room && this.room.roomTypeCode) {
        returnValue = this.$store.getters.getRoomTypeCode(this.room.roomTypeCode);
      }
      return returnValue;
    },
    roomTypeStyle() {
      let returnValue = null;
      if (this.room && this.room.roomTypeCode) {
        const visualizedCodedEntry = this.$store.getters.getRoomTypeCode(this.room.roomTypeCode);
        if (visualizedCodedEntry) {
          returnValue = {
            color: visualizedCodedEntry.foregroundColorHexValue
              ? visualizedCodedEntry.foregroundColorHexValue
              : '#000000',
            marginRight: '5px'
          };
        }
      }
      return returnValue;
    },
    showOccupancyBadge() {
      return this.room.isMaximumOccupancyEnforced || this.room.maximumOccupancyCount > 0;
    },
    transitionGroupNameForStayTiles() {
      return this.$store.getters.transitionGroupNameForStayTiles;
    },
  },
  created() {
    this.$eventbus.$on('CloseAllContextMenus', () => {
      this.isContextMenuVisible = false;
    });
  },
  filters: {
    roomTypeDisplayName: (value) => {
      let returnValue = null;
      if (value) {
        returnValue = value.displayName;
      }
      return returnValue;
    }
  },
  methods: {
    assignClient(params) {
      if (params && params.clientSummary) {
        const newStay = {
          bedBoardAlerts: params.alerts,
          bedBoardStayID: {
            scopeID: this.$store.getters.scopeID,
            id: bedBoardUtils.generateGuid()
          },
          bedID: params.bedID,
          clientSummary: params.clientSummary,
          dischargeStatusCode: params.dischargeStatusCode,
          expectedDischargeDate: params.expectedDischargeDate,
          staffNotes: params.staffNotes,
          stayTypeCode: params.stayTypeCode
        };
        const stayAssignTransactionParams = {
          bedID: newStay.bedID,
          clientID: newStay.clientSummary.clientID,
          roomID: this.room.bedBoardRoomID,
          staffMemberProviderID: {
            scopeID: this.$store.getters.scopeID,
            id: this.$store.getters.userInfo.providerID
          },
          stayID: newStay.bedBoardStayID,
          unitID: this.unit.bedBoardUnitID
        };
        const stayAssignParams = {
          stay: newStay,
          roomID: this.room.bedBoardRoomID,
          unitID: this.unit.bedBoardUnitID
        };

        bedBoardUtils.raiseBedBoardStayAssignRoomTransactionCreated(stayAssignTransactionParams)
          .then(() => {
            bedBoardUtils.sendAssignClientSignal(this, stayAssignParams)
              .then(() => {})
              .catch((err) => {
                this.$log.error(`Error sending AssignClient signal: ${err.message}`);
                this.$eventbus.$emit('showToast', { header: 'Error', message: 'Unable to send assign client signal.', type: 'error' });
              });
            this.$store.commit('assignStay', stayAssignParams);
          })
          .catch((err) => {
            this.$log.error(`Error raising AssignClient transaction: ${err.message}`);
            this.$eventbus.$emit('showToast', { header: 'Error', message: 'Unable to raise the assign client event.', type: 'error' });
          });
      }
      this.closeAssignClientDialog();
    },

    closeAssignClientDialog() {
      this.isAssignClientDialogVisible = false;
    },

    closeMoveConfirmationDialog() {
      this.isMoveConfirmationDialogVisible = false;
    },

    closeRoomDialog() {
      this.isRoomDialogVisible = false;
    },

    getDragTransferData(stay) {
      return {
        bedID: { id: stay.bedID && stay.bedID.id ? stay.bedID.id : null },
        bedName:
          stay.bedID && stay.bedID.id && this.room.beds && this.room.beds.length > 0
          && this.room.beds.find(bed => bed.bedBoardBedID.id === stay.bedID.id)
            ? this.room.beds.find(bed => bed.bedBoardBedID.id === stay.bedID.id)
              .name
            : null,
        beds: this.$store.getters.getAvailableBeds(
          this.room.bedBoardRoomID,
          stay.bedBoardStayID
        ),
        clientID: stay.clientSummary.clientID,
        clientName: stay.clientSummary.clientName,
        roomID: this.room.bedBoardRoomID,
        roomName: this.room.name,
        staffMemberProviderID: {
          scopeID: this.$store.getters.scopeID,
          id: this.$store.getters.userInfo.providerID
        },
        stayID: stay.bedBoardStayID,
        unitID: this.unit.bedBoardUnitID,
      };
    },

    isStayActionable(stay) {
      return this.$store.getters.isStayActionable(stay);
    },

    handleRoomDragover(data, event) {
      if (data && (!this.unit.isMoveStayAllowed)) {
        event.dataTransfer.dropEffect = 'none';
      }
    },

    handleRoomDrop(stayTransferData, event) {
      event.stopPropagation();
      if (bedBoardUtils.isRoomMaxOccupancyExceeded(this.room, this.$store)) {
        this.$dialog.alert('At maximum capacity for room.', { animation: 'fade' });
      } else if (stayTransferData) {
        this.moveParams = {
          fromValues: stayTransferData,
          toValues: {
            beds: this.$store.getters.getAvailableBeds(this.room.bedBoardRoomID),
            bedID: { id: null },
            roomName: this.room.name,
            roomID: this.room.bedBoardRoomID,
            unitID: this.unit.bedBoardUnitID
          }
        };
        this.isMoveConfirmationDialogVisible = true;
      } else {
        this.$log.error(`[${moment().format(appSettings.logDateFormat)}] No stay data provided for drop.`);
      }
    },
    handleStayDragEnd() {
      this.$eventbus.$emit('roomStayDragEnded');
    },
    handleStayDragStart() {
      this.$eventbus.$emit('roomStayDragStarted');
    },
    moveStay(params) {
      this.closeMoveConfirmationDialog();
      bedBoardUtils.raiseBedBoardMoveStayTransactionCreated(params)
        .then(() => {
          bedBoardUtils.sendMoveStaySignal(this, params)
            .then(() => {})
            .catch((err) => {
              this.$log.error(`Error sending MoveStay signal: ${err.message}`);
              this.$eventbus.$emit('showToast', { header: 'Error', message: 'Unable to send move stay signal.', type: 'error' });
            });
          this.$store.commit('moveStay', params);
        })
        .catch((err) => {
          this.$log.error(`Error raising MoveStay transaction: ${err.message}`);
          this.$eventbus.$emit('showToast', { header: 'Error', message: 'Unable to raise the move stay event.', type: 'error' });
        });
    },
    saveRoom(updatedRoom) {
      const params = {
        roomID: updatedRoom.bedBoardRoomID,
        values: {
          note: updatedRoom.currentNote,
          roomStatusCode: updatedRoom.roomStatusCode,
          roomTypeCode: updatedRoom.roomTypeCode
        }
      };

      updatedRoom.auditInformation = {
        lastUpdatedByStaffMemberID: {
          scopeID: this.$store.getters.scopeID,
          id: this.$store.getters.userInfo.providerID
        },
        lastUpdatedDate: moment().format()
      };

      bedBoardUtils.raiseBedBoardRoomUpdated(updatedRoom)
        .then(() => {
          bedBoardUtils.sendUpdateRoomValuesSignal(this, params)
            .then(() => {})
            .catch((err) => {
              this.$log.error(`Error sending UpdateRoomValues signal: ${err.message}`);
              this.$eventbus.$emit('showToast', { header: 'Error', message: 'Unable to send updated room signal.', type: 'error' });
            });
          this.$store.commit('updateRoomValues', params);
        })
        .catch((err) => {
          this.$log.error(`Error raising RoomUpdated transaction: ${err.message}`);
          this.$eventbus.$emit('showToast', { header: 'Error', message: 'Unable to raise the updated room event.', type: 'error' });
        });

      this.closeRoomDialog();
    },

    showAssignClientDialog() {
      if (bedBoardUtils.isUnitMaxOccupancyExceeded(this.unit)) {
        this.$dialog.alert('At maximum capacity for unit.', { animation: 'fade' });
      } else if (bedBoardUtils.isRoomMaxOccupancyExceeded(this.room, this.$store)) {
        this.$dialog.alert('At maximum capacity for room.', { animation: 'fade' });
      } else {
        this.isAssignClientDialogVisible = true;
      }
    },

    showContextMenu(e) {
      this.$eventbus.$emit('CloseAllContextMenus');
      e.preventDefault();
      this.x = e.clientX;
      this.y = e.clientY;
      this.isContextMenuVisible = false;

      this.$nextTick(() => {
        this.isContextMenuVisible = true;
      });
    },

    showRoomDialog() {
      this.roomCopy = bedBoardUtils.clone(this.room);
      this.isRoomDialogVisible = true;
    }
  }
};
</script>

<style lang="scss">
.addUserButton {
  color: #003b5c !important;
}
.disabled {
  opacity: 0.3;
  pointer-events: none;
}
.drag {
  background-color: #ffffff;
}
.drop.allowed {
  background-color: pink;
}
.drop.over {
  border-color: purple;
  background: pink;
}
.roomBadge {
  background-color: red;
}
.roomName {
  font-family: "Lato";
  font-size: 16px;
  font-weight: 700;
  margin-right: 5px;
  vertical-align: middle;
}
.roomNote {
  vertical-align: middle;
}
.roomTileContainer {
  padding-bottom: 2px;
}
.roomTileHeader {
  cursor: default;
  padding: 9px 9px 6px 9px;
}
.roomType {
  color: #333333;
  font-family: "Lato";
  font-size: 16px;
  font-weight: 700;
  vertical-align: middle;
}

.stayContainer {
  background-color: #ebebeb;
  // min-height: 71px;
  // height: 100%;
  margin: 0px 5px 2px 5px;
  padding: 3px 0px 1px 0px;
}

.stayTile {
  background-color: #ffffff;
  cursor: default;
  margin: 0px 3px 3px 3px;
  padding: 3px 5px 3px 5px;
}

.tg-stay-tiles-enter-active {
  transition: all 1s;
}
.tg-stay-tiles-leave-active {
  background-color: red;
  transition: all 1s;
}
.tg-stay-tiles-enter {
  background-color: green;
  opacity: 0;
}
.tg-stay-tiles-leave-to {
  opacity: 0;
}
</style>
